import React, {createContext, useCallback, useContext, useEffect, useMemo, useState} from "react";
import {fabric} from "fabric";
import {v4 as uuid} from "uuid";
import {StoredIndividualization, StoredSpot, StoredTemplate} from "../types/Individualization";
import {MediaCategory, MediaWithCategories} from "../types/Media";
import {Font} from "../types/Font";
import {SubscriptionData} from "../types/Subscription";
import {useFetchData} from "../hooks/useFetchData";
import {useFetchSingle} from "../hooks/useFetchSingle";
import {resizeFabricObject} from "../helpers/resizeFabricObject";
import {ButtonSetting} from "../types/ButtonSetting";
import {Article} from "../types/Article";
import {useGlobalContext} from "./globalContext";
import {getTranslatedObject} from "../helpers/getTranslatedObject";

type CustomizerContextValues = {
    article: Article|null
    canvas: fabric.Canvas
    individualization: StoredIndividualization
    userInputs: Map<string, fabric.Object>
    setUserInputs: React.Dispatch<React.SetStateAction<Map<string, fabric.Object>>>
    resize: (new_size: number) => void
    activeTemplate: StoredTemplate|undefined
    activeTemplateId: number|null
    setActiveTemplateId: React.Dispatch<React.SetStateAction<number | null>>
    getClipPath: (spot_id: number) => fabric.Object|undefined
    getFabricByUniqueId: (unique_id: string) => fabric.Object|null
    fonts: Font[];
    buttonSetting: ButtonSetting|null;
    uuids: Map<number, string>;
    cuttingMaskElements: Map<number, {uid: string, type: 'text'|'image'}[]>;
    setCuttingMaskElements: React.Dispatch<React.SetStateAction<Map<number, {uid: string, type: 'text'|'image'}[]>>>
    downloadCustomization: () => void
    exportTemplates: () =>  Promise<{template_id: number, base64: string, svg: string
    }>[]
    kArtikel: number|undefined
    spotInMediaOverview: [number, string] | null
    showMediaOverview: (spotData: [number, string] ) => void
    closeMediaOverview: () => void
    media : MediaWithCategories[]
    categories: MediaCategory[]
    isMediaLoading: boolean
    isLoading: boolean
    subscription: SubscriptionData | null
}

const CustomizerContext = createContext<CustomizerContextValues|undefined>(undefined)

export const CustomizerContextProvider: React.FC<{ children: React.ReactNode, individualization: StoredIndividualization, kArtikel: number|undefined}> = (
    {
        children,
        individualization,
        kArtikel
    }
) => {
    const [userInputs, setUserInputs] = useState<Map<string, fabric.Object>>(new Map())
    const [activeTemplateId, setActiveTemplateId] = useState<number|null>(null)
    const [cuttingMaskElements, setCuttingMaskElements] = useState<Map<number, {uid: string, type: 'text'|'image'}[]>>(new Map())
    const [spotInMediaOverview, setSpotInMediaOverview] = useState<[number, string ] | null>(null)
    const { plugin } = useGlobalContext()
    const {
        data: fonts,
        fetch,
    } = useFetchData<Font>('fonts')

    const {
        data: buttonSetting,
        fetch: fetchButton,
        isLoading: isButtonLoading
    } = useFetchSingle<ButtonSetting>('buttonSettings')

    const {
        data: article,
        fetch: fetchArticle,
        isLoading: isArticleLoading
    } = useFetchSingle<Article>('articles')

    const {
        data: media,
        fetch: fetchMedia,
        isLoading: isMediaLoading
    } = useFetchData<MediaWithCategories>('media')

    const {
        data: categories,
        fetch: fetchCategories,
        isLoading: isCategoriesLoading,
    } = useFetchData<MediaCategory>('mediaCategories')

    const {
        data: subscription,
        fetch: fetchSubscription,
        isLoading: isSubscriptionLoading
    } = useFetchSingle<SubscriptionData>('updateSubscription')

    useEffect(() => {
        fetchMedia();
    }, [fetchMedia]);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    useEffect(() => {
        fetchSubscription();
    }, [fetchSubscription]);

    useEffect(() => {
        fetchButton();
    }, [fetchButton]);

    useEffect(() => {
        fetchArticle(kArtikel);
    }, [kArtikel, fetchArticle]);

    const canvas = useMemo(() => {
        return new fabric.Canvas(document.createElement('canvas'), {
            backgroundColor: 'white',
            selection: false,
            renderOnAddRemove: true,
            preserveObjectStacking: true,
        });
    },[])

    const isLoading = useMemo(() => {
        return isCategoriesLoading || isMediaLoading || isSubscriptionLoading || isButtonLoading || isArticleLoading
    }, [isSubscriptionLoading, isCategoriesLoading, isMediaLoading, isButtonLoading, isArticleLoading])

    const showMediaOverview = (spotData : [number, string]) => {
        setSpotInMediaOverview(spotData)
    }
    const closeMediaOverview = () => {
        setSpotInMediaOverview(null)
    }

    const getClipPath = useCallback((spot_id: number) => {
        return canvas.getObjects().find((obj) => {
            return obj.data?.spot_id === spot_id && obj.data?.is_clip_path
        })
    }, [canvas])

    const resize = useCallback((new_size: number) => {
        canvas.setWidth(new_size)
        canvas.setHeight(new_size)

        if (canvas.backgroundImage && canvas.backgroundImage instanceof fabric.Image){
            const scaleRatio = Math.min(
                new_size / canvas.backgroundImage.getOriginalSize().width,
                new_size / canvas.backgroundImage.getOriginalSize().height
            );

            canvas.backgroundImage.set({
                scaleX: scaleRatio,
                scaleY: scaleRatio,
                top: new_size/2,
                left: new_size/2
            })
        }

        canvas.getObjects().forEach((obj) => {
            resizeFabricObject(obj, new_size)
            if (obj.clipPath) resizeFabricObject(obj.clipPath, new_size)
        })

        canvas.requestRenderAll()
    }, [canvas])

    const activeTemplate: StoredTemplate|undefined = useMemo(() => {
        if (subscription === null) {
            return undefined
        }

        const template = individualization.templates.find(template => template.id === activeTemplateId)

        const hasPlan = subscription.plans.filter((p) => p.slug === 'zr-customizer-freier-modus').length > 0

        if (template && !hasPlan ) {
            template.spots = template.spots.filter((s: StoredSpot) => {
                return s.type !== 'cutting mask'
            })
        }

        return template
    }, [activeTemplateId, individualization, subscription])

    const uuids = useMemo(() => {
        const unique_ids = new Map<number, string>()
        individualization.templates.forEach((t) => {
          t.spots.forEach((s) => {
              if (['text', 'image', 'auto text'].includes(s.type)) {
                  unique_ids.set(s.id, uuid())
              }
          })
        })
        return unique_ids;
    },[individualization])

    const setCanvasBackground = useCallback((template: StoredTemplate) => {
        fabric.Image.fromURL(template.src, (fabricImage: fabric.Image) => {
            const scale = canvas.getWidth()

            const img = fabricImage.set({
                originX: 'center',
                originY: 'center',
                centeredScaling: true,
                left: scale / 2,
                top: scale / 2,
            })

            const scaleRatio = Math.min(scale / img.getScaledWidth(), scale / img.getScaledHeight());

            canvas.setBackgroundImage((img as fabric.Image), canvas.renderAll.bind(canvas), {
                scaleX: scaleRatio,
                scaleY: scaleRatio
            });
        });
    }, [canvas])

    const loadClipPaths = useCallback((spot: StoredSpot) => {
        const {type, ...rest} = spot.fabric
        let element: fabric.Circle | fabric.Ellipse | fabric.Rect | fabric.Polygon | fabric.Object = new fabric.Object()
        const spotSettings = {
            ...rest,
            selectable: false,
            strokeDashArray: [10],
            fill: 'transparent',
            stroke: '#525252',
            strokeWidth: 2,
            hoverCursor: 'default',
            data: {
                size: individualization.size,
                spot_id: spot.id,
                template_id: spot.template_id,
                is_clip_path: true
            }
        }

        switch (type) {
            case 'circle':
                element = new fabric.Circle(spotSettings)
                break;
            case 'rect':
                element = new fabric.Rect(spotSettings)
                break;
            case 'ellipse':
                element = new fabric.Ellipse(spotSettings)
                break;
            case 'polygon':
                element = new fabric.Polygon([], spotSettings);
                break;
            default:
                break;
        }

        canvas.add(element)
        const translatedSpot = getTranslatedObject(spot, plugin?.language)
        canvas.add(new fabric.Text(translatedSpot.name, {
            fontSize: 12,
            left: element.oCoords?.tl.x ?? 0,
            top: (element.oCoords?.tl.y ?? 0) - 16,
            fill: '#525252',
            hoverCursor: 'default',
            selectable: false,
            data: {size: individualization.size}
        }))

    }, [canvas, individualization, plugin])

    const loadTemplateUserInputs = useCallback((template: StoredTemplate) => {
        [...userInputs.values()].forEach((obj) => {
            if (obj.data?.template_id === template.id) {
                canvas.add(obj)
            }
        })
    }, [canvas, userInputs])

    const fillCanvas = (template: StoredTemplate) => {
        canvas.clear()
        setCanvasBackground(template)
        template.spots.forEach((spot) => loadClipPaths(spot))
        loadTemplateUserInputs(template)
        resize(canvas.getWidth())
    }

    const getFabricByUniqueId = useCallback((unique_id: string) => {
        const ele = userInputs.get(unique_id)
        return ele ?? null
    }, [userInputs])

    const downloadCustomization = () => {
        Promise.all(exportTemplates()).then((exportFiles) => {
            for(const f of exportFiles) {
                const pseudoLink: HTMLAnchorElement = document.createElement('a')

                pseudoLink.href = f.base64
                pseudoLink.download = "canvas_" + f.template_id + "_" + Date.now().toString() + '.png'
                pseudoLink.click()

                pseudoLink.remove()
            }
        })
    }

    const exportTemplates = useCallback(() => {
        const files: Promise<{template_id: number, base64: string, svg: string}>[] = []

        for (const t of individualization.templates){
            const p = new Promise<{ template_id: number; base64: string; svg: string}>((resolve) => {
                fabric.Image.fromURL(t.src, (fabricImage: fabric.Image) => {
                    const copyFabric = new fabric.Canvas(null, {
                        height: canvas.height,
                        width: canvas.width,
                        renderOnAddRemove: true,
                        backgroundColor: 'white'
                    })

                    const background = fabricImage.set({
                        originX: 'center',
                        originY: 'center',
                        centeredScaling: true,
                        left: copyFabric.width ? (copyFabric.width / 2) : undefined,
                        top: copyFabric.height ? (copyFabric.height / 2) : undefined,
                    });


                    if (background.width && background.height && copyFabric.width) {
                        if (background.width >= background.height) {
                            background.scaleToWidth(copyFabric.width)
                        } else {
                            background.scaleToHeight(copyFabric.width)
                        }
                    }

                    copyFabric.add(background)
                    copyFabric.sendToBack(background);

                    [...userInputs.values()].forEach((obj) => {
                        if (obj.data?.template_id === t.id) {
                            copyFabric.add(obj)
                        }
                    })

                    const base64 = copyFabric.toDataURL({
                            format: "png",
                            quality: 1,
                        })

                    const svg = copyFabric.toSVG();

                    const svgElement = new DOMParser().parseFromString(svg, 'image/svg+xml').documentElement;

                    copyFabric.getObjects().forEach(obj => {
                        console.log('Object after toDataURL/toSVG:', obj);
                    });

                    resolve({template_id: t.id, base64, svg})
                });
            })

            files.push(p)
        }

        return files;
    }, [userInputs, individualization, canvas.height , canvas.width])

    useEffect(() => {
        if(individualization.templates.length > 0) {
            setActiveTemplateId(individualization.templates[0].id)
        }
    }, [individualization])

    useEffect(() => {
        if (activeTemplate) fillCanvas(activeTemplate)
    }, [activeTemplate])

    useEffect(() => {
        fetch()
    }, [fetch])

    return (
        <CustomizerContext.Provider value={{
            kArtikel,
            article,
            canvas,
            isLoading,
            individualization,
            userInputs,
            setUserInputs,
            resize,
            activeTemplate,
            activeTemplateId,
            setActiveTemplateId,
            getClipPath,
            getFabricByUniqueId,
            fonts,
            buttonSetting,
            uuids,
            cuttingMaskElements,
            setCuttingMaskElements,
            downloadCustomization,
            exportTemplates,
            spotInMediaOverview,
            showMediaOverview,
            closeMediaOverview,
            media,
            categories,
            isMediaLoading,
            subscription
        }}>
            {children}
        </CustomizerContext.Provider>
    )
}

export const useCustomizer = () => {
    const data = useContext(CustomizerContext)

    if (data === undefined) {
        throw new Error('useCustomizer must used within CustomizerContextProvider')
    }

    return data
}
